import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const isSupabaseConfigured = !!(supabaseUrl && supabaseAnonKey);

export const supabase = isSupabaseConfigured
  ? createClient(supabaseUrl, supabaseAnonKey)
  : null;

export const checkSupabaseConnection = async () => {
  if (!isSupabaseConfigured) {
    console.warn('Supabase not configured. Running in demo mode.');
    return false;
  }

  try {
    const { error } = await supabase
      .from('quote')
      .select('*', { count: 'exact', head: true });

    if (error) {
      console.warn('Supabase connection issue:', error.message);
      return false;
    }

    return true;
  } catch (err) {
    console.warn('Supabase connection failed:', err.message);
    return false;
  }
};

export const insertData = async (table, data) => {
  if (!isSupabaseConfigured) {
    console.log(`[Demo Mode] Would insert into ${table}:`, data);
    return {
      data: { id: Date.now(), ...data },
      error: null,
      demo: true,
    };
  }

  const { data: result, error } = await supabase
    .from(table)
    .insert([data])
    .select()
    .single();

  return {
    data: result,
    error,
    demo: false,
  };
};

export const selectData = async (table, options = {}) => {
  if (!isSupabaseConfigured) {
    console.log(`[Demo Mode] Would select from ${table}`);
    return { data: [], error: null, demo: true };
  }

  let query = supabase.from(table).select(options.columns || '*');

  if (options.filters) {
    Object.entries(options.filters).forEach(([key, value]) => {
      query = query.eq(key, value);
    });
  }

  if (options.inFilters) {
    Object.entries(options.inFilters).forEach(([key, values]) => {
      query = query.in(key, values);
    });
  }

  if (options.gteFilters) {
    Object.entries(options.gteFilters).forEach(([key, value]) => {
      query = query.gte(key, value);
    });
  }

  if (options.lteFilters) {
    Object.entries(options.lteFilters).forEach(([key, value]) => {
      query = query.lte(key, value);
    });
  }

  if (options.order) {
    query = query.order(options.order.column, {
      ascending: options.order.ascending ?? false,
    });
  }

  if (options.limit) {
    query = query.limit(options.limit);
  }

  const { data, error } = await query;

  return {
    data: data || [],
    error,
    demo: false,
  };
};

export const invokeFunction = async (functionName, payload = {}) => {
  if (!isSupabaseConfigured) {
    console.log(`[Demo Mode] Would invoke function ${functionName}:`, payload);
    return {
      data: null,
      error: null,
      demo: true,
    };
  }

  try {
    const { data, error } = await supabase.functions.invoke(functionName, {
      body: payload,
    });

    return {
      data,
      error,
      demo: false,
    };
  } catch (err) {
    return {
      data: null,
      error: err,
      demo: false,
    };
  }
};

export default supabase;